import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, UserRole } from '../types/user';

interface UserGoals {
  platform: 'instagram' | 'youtube' | 'tiktok';
  username: string;
  followers: number;
  goal: string;
  targetFollowers: number;
  targetViews: number;
}

interface UserStore {
  currentUser: User | null;
  registeredUsers: User[];
  hasSeenSocialPrompt: boolean;
  isOnboardingCompleted: boolean;
  userGoals: UserGoals | null;
  setUser: (user: User | null) => void;
  registerUser: (user: User) => void;
  findUserByEmail: (email: string) => User | undefined;
  validateCredentials: (email: string, password: string) => User | null;
  isAdmin: () => boolean;
  isPremium: () => boolean;
  isPro: () => boolean;
  hasUnlimitedAccess: () => boolean;
  setSocialPromptSeen: () => void;
  updateUserRole: (email: string, role: UserRole) => void;
  setRole: (userId: string, role: UserRole) => void;
  removeUser: (userId: string) => void;
  getAllUsers: () => User[];
  setOnboardingComplete: (completed: boolean) => void;
  setUserGoals: (goals: UserGoals) => void;
  getUserGoals: () => UserGoals | null;
}

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      currentUser: null,
      registeredUsers: [
        {
          id: '1',
          name: 'Admin User',
          email: 'danieldorncarvalho@gmail.com',
          password: 'Dani4589$?',
          role: 'admin',
          isNewUser: false,
          status: 'active',
          createdAt: new Date().toISOString()
        }
      ],
      hasSeenSocialPrompt: false,
      isOnboardingCompleted: false,
      userGoals: null,
      
      setUser: (user) => set({ currentUser: user }),
      
      registerUser: (user) => set((state) => ({
        registeredUsers: [...state.registeredUsers, user]
      })),

      findUserByEmail: (email) => {
        return get().registeredUsers.find(user => user.email === email);
      },

      validateCredentials: (email, password) => {
        const user = get().registeredUsers.find(user => 
          user.email === email && user.password === password
        );
        return user || null;
      },

      isAdmin: () => {
        const user = get().currentUser;
        if (!user) return false;
        return user.role === 'admin';
      },
      
      isPremium: () => {
        const user = get().currentUser;
        if (!user) return false;
        // Admin users have access to all premium features
        if (user.role === 'admin') return true;
        return user.role === 'premium' || user.role === 'premium_yearly';
      },
      
      isPro: () => {
        const user = get().currentUser;
        if (!user) return false;
        // Admin users have access to all pro features
        if (user.role === 'admin') return true;
        return user.role === 'pro' || user.role === 'pro_yearly';
      },
      
      hasUnlimitedAccess: () => {
        const user = get().currentUser;
        if (!user) return false;
        // Admin users always have unlimited access
        if (user.role === 'admin') return true;
        return ['premium', 'premium_yearly', 'pro', 'pro_yearly'].includes(user.role);
      },

      setSocialPromptSeen: () => set({ hasSeenSocialPrompt: true }),

      updateUserRole: (email, role) => {
        console.log(`Updating user role: ${email} -> ${role}`);
        set((state) => {
          // Update in registered users
          const updatedUsers = state.registeredUsers.map(user => 
            user.email === email ? { ...user, role } : user
          );

          // Update current user if it's the same email
          const updatedCurrentUser = state.currentUser?.email === email ? 
            { ...state.currentUser, role } : 
            state.currentUser;

          return {
            registeredUsers: updatedUsers,
            currentUser: updatedCurrentUser
          };
        });
      },
      
      setRole: (userId, role) => set((state) => {
        console.log(`Setting role for user ${userId} to ${role}`);
        // Update in registered users
        const updatedUsers = state.registeredUsers.map(user => 
          user.id === userId ? { ...user, role } : user
        );

        // Update current user if it's the same id
        const updatedCurrentUser = state.currentUser?.id === userId ? 
          { ...state.currentUser, role } : 
          state.currentUser;

        return {
          registeredUsers: updatedUsers,
          currentUser: updatedCurrentUser
        };
      }),
      
      removeUser: (userId) => set((state) => ({
        registeredUsers: state.registeredUsers.filter(user => user.id !== userId)
      })),
      
      getAllUsers: () => get().registeredUsers,

      setOnboardingComplete: (completed) => set({ isOnboardingCompleted: completed }),

      setUserGoals: (goals) => set({ userGoals: goals }),

      getUserGoals: () => get().userGoals
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => localStorage),
      version: 4,
      migrate: (persistedState: any, version: number) => {
        if (version < 4) {
          return {
            ...persistedState,
            isOnboardingCompleted: false,
            userGoals: null,
            registeredUsers: [
              ...(persistedState.registeredUsers || []),
              {
                id: '1',
                name: 'Admin User',
                email: 'danieldorncarvalho@gmail.com',
                password: 'Dani4589$?',
                role: 'admin',
                isNewUser: false,
                status: 'active',
                createdAt: new Date().toISOString()
              }
            ]
          };
        }
        return persistedState;
      }
    }
  )
);

// Listen for role updates from webhook
if (typeof window !== 'undefined') {
  window.addEventListener('message', (event) => {
    if (event.data.type === 'UPDATE_USER_ROLE') {
      const { email, role } = event.data.data;
      useUserStore.getState().updateUserRole(email, role);
    }
  });
}